import apiService from './api';
import type { ApiResponse } from '../types/api';
import type { ChemicalInventoryLocation, PODocument } from '../types/spray';
import { openDocument } from '../utils/openDocument';

/**
 * Who uses purchase orders, and the few things the pages need that are not
 * the orders themselves. The orders are still read and written through
 * `sprayService` (`/spray/purchase-orders`, from when they were a tab of
 * Spray); purchase orders now belong to no module, and who uses them is the
 * organisation admin's decision.
 */

export interface PurchaseOrderAccess {
  /** Authorised by the organisation's admin: raise, send, receive, cancel. */
  can_raise: boolean;
  /** Authorised, or holding an Accounts seat. */
  can_read: boolean;
  /** An organisation admin: says who is authorised. */
  can_manage: boolean;
}

export interface PurchaseOrderPerson {
  id: number;
  first_name: string | null;
  last_name: string | null;
  email: string;
  authorised: boolean;
}

class PurchaseOrderService {
  async getAccess(): Promise<ApiResponse<PurchaseOrderAccess>> {
    return apiService.get('/spray/purchase-orders/access');
  }

  /** Organisation admin only: the organisation's people, each with whether they are authorised. */
  async getPeople(): Promise<ApiResponse<PurchaseOrderPerson[]>> {
    return apiService.get('/spray/purchase-orders/authorised-users');
  }

  /** Organisation admin only: replace the authorised people. */
  async setAuthorised(userIds: number[]): Promise<ApiResponse<PurchaseOrderPerson[]>> {
    return apiService.put('/spray/purchase-orders/authorised-users', { user_ids: userIds });
  }

  /** Attach a payment receipt (a photo or PDF) to an order. */
  async addReceipt(poId: number, file: File, note?: string): Promise<ApiResponse<PODocument>> {
    const form = new FormData();
    form.append('file', file);
    if (note) form.append('note', note);
    // A phone photo over paddock signal: well past the default 10 s.
    return apiService.post(`/spray/purchase-orders/${poId}/documents`, form, undefined, { timeout: 120000 });
  }

  async removeReceipt(docId: number): Promise<ApiResponse<{ id: number; deleted: boolean }>> {
    return apiService.delete(`/spray/purchase-orders/documents/${docId}`);
  }

  /** The route needs the bearer token, so the file cannot be a plain link. */
  async openReceipt(doc: Pick<PODocument, 'id' | 'file_name'>): Promise<void> {
    await openDocument(`/spray/purchase-orders/documents/${doc.id}/file`, doc.file_name || `receipt-${doc.id}`);
  }

  /** Where received chemical stock can be put. */
  async getLocations(): Promise<ApiResponse<ChemicalInventoryLocation[]>> {
    return apiService.get('/spray/purchase-orders/locations');
  }
}

export default new PurchaseOrderService();
